import { useMutation, useQueryClient } from "@tanstack/react-query"

import { useStableFunction } from "@repo/ui/hooks/use-stable-function"
import { mapSupabaseUserToAuthUser } from "../utils"
import { queryKey as sessionQueryKey } from "./use-session"
import { useSupabase } from "./use-supabase"
import { queryKey as userQueryKey } from "./use-user"

const mutationKey = ["auth", "refresh-session"]

type RefreshSessionParams = {
  refresh_token: string
}

export type UseRefreshSessionProps = {
  onSuccess?: VoidFunction
  onError?: (error: Error) => void
}

export function useRefreshSession({
  onSuccess,
  onError,
}: UseRefreshSessionProps = {}) {
  const supabase = useSupabase()
  const queryClient = useQueryClient()

  const mutationFn = useStableFunction(
    async (params?: RefreshSessionParams) => {
      const { data, error } = await supabase.auth.refreshSession(params)

      if (error) {
        throw error
      }

      await queryClient.setQueryData(sessionQueryKey, data.session)
      await queryClient.setQueryData(
        userQueryKey,
        data.user && mapSupabaseUserToAuthUser(data.user),
      )

      return data
    },
  )

  return useMutation({
    mutationKey,
    mutationFn,
    onSuccess,
    onError,
  })
}
